<template>
  <div class="register-area">
    <loader v-show="isFormLoading"></loader>
    <div v-show="!isFormLoading">
      <b-form @change="show=true" @submit="onSubmit">
        <error :errors="errors"/>
        <div class="temp-note">{{ $t('createcompanyaccount.limitedLiabilityCompanies') }}</div>
        <div>{{ $t('createcompanyaccount.companyRepresentative') }}</div>
        <div>{{ $t('createcompanyaccount.pleaseEnterYour') }}</div>
        <div class="inline-group">
          <b-form-group class="w-50 first-inline-field">
            <b-form-input class="name-field"
                          id="input-company-first-name"
                          v-model="form.first_name"
                          required
                          :state="firstNameState"
                          @change="setChanged('first_name')"
                          :placeholder="$t('createcompanyaccount.firstName')"
                          trim
                          aria-describedby="input-company-first-name-feedback"
                          :formatter="onlyLatinFormatter"
            ></b-form-input>
            <b-form-invalid-feedback id="input-company-first-name-feedback">{{ $t('createcompanyaccount.enterAtLeast') }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group class="w-50 second-inline-field">
            <b-form-input class="name-field"
                          id="input-company-last-name"
                          v-model="form.last_name"
                          :state="lastNameState"
                          @change="setChanged('last_name')"
                          required
                          :placeholder="$t('createcompanyaccount.lastName')"
                          trim
                          aria-describedby="input-last-name-feedback"
                          :formatter="onlyLatinFormatter"
            ></b-form-input>
            <b-form-invalid-feedback id="input-last-name-feedback">{{ $t('createcompanyaccount.enterAtLeast1') }}</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <b-form-group>
          <vue-tel-input
              @country-changed="syncCountries"
              class="ca-field tel-field"
              :placeholder="$t('createcompanyaccount.mobileNumber')"
              required
              v-bind="telOptions"
              @onInput="phoneInput"
              v-model="form.phone">
          </vue-tel-input>
        </b-form-group>

        <b-form-group class="form-group" v-if="items">
          <label>{{ $t('createcompanyaccount.selectCountryOfResidence') }}</label>
          <cool-select
              required
              v-model="selectedCountry"
              :items="items"
              :placeholder="selectedCountry ? '' : $t('app.selectCountry')"
              item-value="short_name"
              :item-text="$t('createcompanyaccount.name')"
              aria-required="true"
              class="country-select form-field"
              :class="submitted && !selectedCountry? ' form-control is-invalid': ''"
          >
            <!-- slot for each item in the menu -->
            <template slot="item" slot-scope="{ item: country }">
              <div style="display: flex; align-items: center;">
                <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">

                <div>
                  <b>{{ country.name }}</b>
                </div>
              </div>
            </template>
            <!-- slot for the selected item (in the text field) -->
            <template slot="selection" slot-scope="{ item: country }">
              <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">
              <div>
                <b>{{ country.name }}</b>
              </div>
            </template>
          </cool-select>
        </b-form-group>

        <b-form-group class="form-group">
          <b-form-input class="form-field"
                        id="input-street"
                        v-model="form.addr_street"
                        :state="streetState"
                        @change="setChanged('street')"
                        required
                        :placeholder="$t('createcompanyaccount.residenceAddress')"
                        trim
                        aria-describedby="input-street-feedback"
                        :formatter="onlyLatinFormatter"
          ></b-form-input>
          <b-form-invalid-feedback id="input-street-feedback">{{ $t('createcompanyaccount.enterAtLeast2') }}</b-form-invalid-feedback>
        </b-form-group>

        <div class="inline-group">
          <b-form-group class="form-group w-50 first-inline-field">
            <b-form-input class="form-field"
                          id="input-city"
                          v-model="form.addr_city"
                          :state="cityState"
                          @change="setChanged('city')"
                          required
                          :placeholder="$t('createcompanyaccount.city')"
                          trim
                          aria-describedby="input-city-feedback"
                          :formatter="onlyLatinFormatter"
            ></b-form-input>
            <b-form-invalid-feedback id="input-city-feedback">{{ $t('createcompanyaccount.enterAtLeast3') }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group class="form-group w-50">
            <b-form-input class="form-field"
                          id="input-postal-code"
                          v-model="form.addr_zip"
                          :state="postalCodeState"
                          @change="setChanged('postal_code')"
                          required
                          :placeholder="$t('createcompanyaccount.postalCode')"
                          trim
                          aria-describedby="input-postal_code-feedback"
                          :formatter="onlyLatinAndNumbersFormatter"
            ></b-form-input>
            <b-form-invalid-feedback id="input-postal_code-feedback">{{ $t('createcompanyaccount.enterAtLeast4') }}</b-form-invalid-feedback>
          </b-form-group>
        </div>
        <b-form-group>
          <label>{{ $t('createcompanyaccount.rightOfRepresentation') }}</label>
          <b-form-select v-model="form.right_of_representation" :options="this.translate(right_of_representation_options)" class="mb-3" required>
          </b-form-select>
        </b-form-group>
        <label>{{ $t('createcompanyaccount.companyDetails') }}</label>
        <div class="inline-group">
          <b-form-group class="w-50 first-inline-field">
            <b-form-input autofocus class="name-field"
                          id="input-company-name"
                          v-model="form.comp_name"
                          required
                          :state="companyNameState"
                          @change="setChanged('name')"
                          :placeholder="$t('createcompanyaccount.companyName')"
                          trim
                          aria-describedby="input-company-name-feedback"
                          :formatter="onlyLatinFormatter"
            ></b-form-input>
            <b-form-invalid-feedback id="input-company-name-feedback">{{ $t('createcompanyaccount.enterAtLeast5') }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group class="w-50 second-inline-field">
            <b-form-input class="name-field"
                          id="input-company-number"
                          v-model="form.comp_reg_no"
                          :state="companyNumberState"
                          @change="setChanged('reg_no')"
                          required
                          :placeholder="$t('createcompanyaccount.registrationNr')"
                          trim
                          aria-describedby="input-company-number-feedback"
                          :formatter="onlyLatinFormatter"
            ></b-form-input>
            <b-form-invalid-feedback id="input-company-number-feedback">{{ $t('createcompanyaccount.enterAtLeast6') }}</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <b-form-group class="form-group" v-if="items">
          <label>{{ $t('createcompanyaccount.companyRegisteredAddress') }}</label>
          <cool-select
              required
              v-model="selectedCompanyCountry"
              :items="items"
              :placeholder="selectedCompanyCountry ? '' : $t('app.selectCountry')"
              item-value="short_name"
              :item-text="$t('createcompanyaccount.name1')"
              aria-required="true"
              class="country-select form-field"
              :class="submitted && !selectedCompanyCountry? ' form-control is-invalid': ''"
          >
            <!-- slot for each item in the menu -->
            <template slot="item" slot-scope="{ item: country }">
              <div style="display: flex; align-items: center;">
                <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">

                <div>
                  <b>{{ country.name }}</b>
                </div>
              </div>
            </template>
            <!-- slot for the selected item (in the text field) -->
            <template slot="selection" slot-scope="{ item: country }">
              <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">
              <div>
                <b>{{ country.name }}</b>
              </div>
            </template>
          </cool-select>
        </b-form-group>

        <b-form-group class="form-group">
          <b-form-input class="form-field"
                        id="input-street"
                        v-model="form.comp_addr_street"
                        :state="companyStreetState"
                        @change="setChanged('company_street')"
                        required
                        :placeholder="$t('createcompanyaccount.address')"
                        trim
                        aria-describedby="input-street-feedback"
                        :formatter="onlyLatinFormatter"
          ></b-form-input>
          <b-form-invalid-feedback id="input-street-feedback">{{ $t('createcompanyaccount.enterAtLeast7') }}</b-form-invalid-feedback>
        </b-form-group>

        <div class="inline-group">
          <b-form-group class="form-group w-50 first-inline-field">
            <b-form-input class="form-field"
                          id="input-city"
                          v-model="form.comp_addr_city"
                          :state="companyCityState"
                          @change="setChanged('company_city')"
                          required
                          :placeholder="$t('createcompanyaccount.city1')"
                          trim
                          aria-describedby="input-city-feedback"
                          :formatter="onlyLatinFormatter"
            ></b-form-input>
            <b-form-invalid-feedback id="input-city-feedback">{{ $t('createcompanyaccount.enterAtLeast8') }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group class="form-group w-50">
            <b-form-input class="form-field"
                          id="input-postal-code"
                          v-model="form.comp_addr_zip"
                          :state="companyPostalCodeState"
                          @change="setChanged('company_postal_code')"
                          required
                          :placeholder="$t('createcompanyaccount.postalCode1')"
                          trim
                          aria-describedby="input-postal_code-feedback"
                          :formatter="onlyLatinAndNumbersFormatter"
            ></b-form-input>
            <b-form-invalid-feedback id="input-postal_code-feedback">{{ $t('createcompanyaccount.enterAtLeast9') }}</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <b-form-group v-if="!investorData.eligible_for_utm_bonus">
          <b-form-checkbox name="invitation_code" v-model="invitationCodeChecked">{{ $t('createcompanyaccount.invitationCodeOptional') }}</b-form-checkbox>
          <b-form-input
              class="ca-field invitation-code"
              v-model="form.invitation_code"
              type="text"
              trim
              :placeholder="$t('createcompanyaccount.invitationCode')"
              v-show="invitationCodeChecked"
              :formatter="onlyLatinFormatter"
          ></b-form-input>
        </b-form-group>
        <b-form-group>
          <b-form-checkbox name="agreement" v-model="form.agreement" class="confirm-agreement" required>{{ $t('createcompanyaccount.iHaveRead') }}<a href="useragreement" target="_blank" class="agreement-link">{{ $t('createcompanyaccount.userAgreement') }}</a>{{ $t('createcompanyaccount.and') }}<a href="privacy" target="_blank" class="agreement-link">{{ $t('createcompanyaccount.privacyPolicy') }}</a>
          </b-form-checkbox>
        </b-form-group>
        <b-form-group>
          <b-form-checkbox name="subscription_best_results" v-model="form.subscription_best_results">{{ $t('createcompanyaccount.signUpTo') }}</b-form-checkbox>
        </b-form-group>
        <div class="w-100 text-right">
          <b-button type="submit" class="income-btn register-btn" variant="primary">{{ $t('createcompanyaccount.register') }}</b-button>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import {CoolSelect} from 'vue-cool-select'
import {VueTelInput} from 'vue-tel-input'
import flagMixin from '../mixins/flag'
import telConfig from '../config/telephone.json'
import Error from './Error'
import company from '../api/company'
import classificatorDataHelper from '../helpers/classificatorDataHelper'
import Loader from '@/components/Loader'
import textMixin from '../mixins/text'
import localStorageHelper from '@/helpers/localStorageHelper'

export default {
  name: 'CreateCompanyAccount',
  components: {
    Loader,
    CoolSelect,
    VueTelInput,
    Error
  },
  mixins: [flagMixin, textMixin],
  props: ['investorData'],
  data()
  {
    return {
      form: {
        first_name: '',
        last_name: '',
        agreement: false,
        subscription_best_results: false,
        invitation_code: '',
        addr_country: '',
        addr_zip: '',
        addr_street: '',
        addr_city: '',
        comp_addr_country: '',
        comp_addr_zip: '',
        comp_addr_street: '',
        comp_addr_city: '',
        phone: '',
        type: 'company',
        is_company: true,
        right_of_representation: null
      },
      changed: [],
      show: false,
      submitted: false,
      internationalPhoneNumber: null,
      invitationCodeChecked: false,
      selectedCountry: null,
      selectedCompanyCountry: null,
      items: null,
      telOptions: telConfig,
      companyAddressId: null,
      errors: [],
      isFormLoading: false,
      right_of_representation_options: []
    }
  },
  mounted()
  {
    if (this.$store.state.classificators.classificators)
    {
      this.items = classificatorDataHelper.getCountriesWithPopularInTop()
      this.right_of_representation_options = classificatorDataHelper.getGroupItemsAsOptions('RIGHT_OF_REPRESENTATION_IN_COMPANY')
    }
    if (this.investorData)
    {
      this.fillFormValues(this.investorData)
    }
  },
  watch: {
    selectedCountry: function (selected)
    {
      this.form.addr_country = selected
    },
    selectedCompanyCountry: function (selected)
    {
      this.form.comp_addr_country = selected
    },
    investorData: function (investorData)
    {
      if (investorData)
      {
        this.fillFormValues(investorData)
      }
    }
  },
  computed: {
    companyNameState()
    {
      return this.isChanged('name') ? this.form.comp_name.length > 2 : null
    },
    companyNumberState()
    {
      return this.isChanged('reg_no') ? this.form.comp_reg_no.length > 2 : null
    },
    firstNameState()
    {
      return this.isChanged('first_name') ? this.form.first_name.length > 2 : null
    },
    lastNameState()
    {
      return this.isChanged('last_name') ? this.form.last_name.length > 2 : null
    },
    cityState()
    {
      return this.isChanged('city') ? this.form.addr_city.length > 2 : null
    },
    streetState()
    {
      return this.isChanged('street') ? this.form.addr_street.length > 2 : null
    },
    postalCodeState()
    {
      return this.isChanged('postal_code') ? this.form.addr_zip.length > 2 : null
    },
    companyCityState()
    {
      return this.isChanged('company_city') ? this.form.comp_addr_city.length > 2 : null
    },
    companyStreetState()
    {
      return this.isChanged('company_street') ? this.form.comp_addr_street.length > 2 : null
    },
    companyPostalCodeState()
    {
      return this.isChanged('company_postal_code') ? this.form.comp_addr_zip.length > 2 : null
    }
  },
  methods: {
    onSubmit(evt)
    {
      this.submitted = true
      evt.preventDefault()

      if (!this.form.addr_country || !this.form.comp_addr_country)
      {
        this.$emit('setstep', 1)
        return false
      }
      else
      {
        const countries = classificatorDataHelper.getCountriesWithPopularInTop()
        if (countries)
        {
          let country = countries.filter(c => c.short_name === this.form.addr_country)
          if (country && country[0])
          {
            if (country[0].id)
            {
              this.form.addr_country = country[0].id
            }
          }
          country = countries.filter(c => c.short_name === this.form.comp_addr_country)
          if (country && country[0])
          {
            if (country[0].id)
            {
              this.form.comp_addr_country = country[0].id
            }
          }
        }
      }
      if (!this.invitationCodeChecked)
      {
        this.form.invitation_code = ''
      }
      this.isFormLoading = true
      this.$emit('updateinvestor', this.form)

      const phoneBuffer = this.form.phone
      if (this.internationalPhoneNumber)
      {
        this.form.phone = this.internationalPhoneNumber
      }
      if (!this.investorData || !this.investorData.investor_first_name)
      {
        company.create(this.form)
            .then((response) =>
            {
              if (response)
              {
                window.dataLayer.push({event: 'registration-success'})
                this.$store.state.investors.investor.investor_account_status = response.investor_account_status
                this.$store.state.investors.investor.problems = response.problems
                this.$emit('setstep', 2)
                window.scrollTo(0, 0)
                localStorageHelper.remove('invitation_code')
              }
            })
            .catch((error) =>
            {
              this.errors.push(error)
            })
            .then(() =>
            {
              this.isFormLoading = false
              this.form.phone = phoneBuffer
            })
      }
      else
      {
        company.update(this.form)
            .then((response) =>
            {
              if (response)
              {
                window.dataLayer.push({event: 'registration-success'})
                this.$store.state.investors.investor.investor_account_status = response.investor_account_status
                this.$store.state.investors.investor.problems = response.problems
                this.$emit('setstep', 2)
                window.scrollTo(0, 0)
                localStorageHelper.remove('invitation_code')
              }
            })
            .catch((error) =>
            {
              this.errors.push(error)
            })
            .then(() =>
            {
              this.isFormLoading = false
              this.form.phone = phoneBuffer
            })
      }
    },
    setChanged(id)
    {
      this.changed.push(id)
    },
    isChanged(id)
    {
      return this.changed.includes(id)
    },
    syncCountries(value)
    {
      try
      {
        if (!this.companyAddressId)
        {
          this.selectedCountry = value.iso2
        }
      }
      catch (e)
      {
        console.log('Something went wrong with changing selected country', e)
      }
    },
    phoneInput({number})
    {
      this.internationalPhoneNumber = number.international
    },
    fillFormValues(investorData)
    {
      this.form = {
        first_name: this.getFirstName(),
        last_name: this.getLastName(),
        agreement: investorData.investor_agreement === 1 || investorData.investor_agreement === true,
        subscription_best_results: investorData.subscription_best_results === 1 || investorData.subscription_best_results === true,
        invitation_code: investorData.investor_invitation_code || '',
        type: 'company',
        is_company: true,
        right_of_representation: null,
        addr_country: '',
        addr_zip: '',
        addr_street: '',
        addr_city: '',
        comp_addr_country: '',
        comp_addr_zip: '',
        comp_addr_street: '',
        comp_addr_city: ''
      }
      this.invitationCodeChecked = !!investorData.investor_invitation_code

      const invitationCode = localStorageHelper.getJson('invitation_code')
      if (invitationCode)
      {
        this.invitationCodeChecked = true
        this.form.invitation_code = invitationCode
      }

      // Fill company fields
      if (investorData.investor_is_company)
      {
        this.form.comp_name = investorData.investor_comp_name ? investorData.investor_comp_name : ''
        this.form.comp_reg_no = investorData.investor_comp_reg_no ? investorData.investor_comp_reg_no : ''
        this.form.phone = investorData.investor_phone ? investorData.investor_phone : ''
        this.form.addr_country = investorData.investor_addr_country ? investorData.investor_addr_country : ''
        this.form.comp_addr_country = investorData.investor_comp_addr_country ? investorData.investor_comp_addr_country : ''
        this.companyAddressId = this.form.addr_country
        this.selectedCountry = classificatorDataHelper.getShortNameById('COUNTRY', investorData.investor_addr_country)
        this.selectedCompanyCountry = classificatorDataHelper.getShortNameById('COUNTRY', investorData.investor_comp_addr_country)
      }
    },
    getFirstName()
    {
      if (this.investorData && this.investorData.investor_first_name)
      {
        return this.investorData.investor_first_name
      }
      else
      {
        if (this.$auth.isAuthenticated && this.$auth.user.given_name)
        {
          return this.$auth.user.given_name
        }
      }
    },
    getLastName()
    {
      if (this.investorData && this.investorData.investor_last_name)
      {
        return this.investorData.investor_last_name
      }
      else
      {
        if (this.$auth.isAuthenticated && this.$auth.user.family_name)
        {
          return this.$auth.user.family_name
        }
      }
    },
    translate(options)
    {
      return options.map(el =>
          {
            if (el.label_i18n)
            {
              el.text = this.$t(el.label_i18n)
            }
            return el
          }
      )
    }
  }
}
</script>
<style lang="scss" scoped>
.invitation-code{
  margin-top: 10px;
}
.country-select {
  margin-top: 6px;
  margin-bottom: 10px;
}
.temp-note{
  font-weight: bold;
  margin-bottom: 15px;
}
</style>
